<script setup lang="ts">
import ProgressSpinner from 'primevue/progressspinner';
import Card from 'primevue/card';
import Toolbar from 'primevue/toolbar';
import { createModel } from '@kp/js-model';
import { computed, onMounted, ref, type Ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import { errToToast, reallyCancel } from '@/helpers/tools';
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRouter } from 'vue-router';
import { getI18n } from '@/helpers/i18n';
import KpButton from '@/components/KpButton.vue';
import KpNumberField from '@/components/KpNumberField.vue';
import { KorrekturWaermeerzeugerHeizung } from '@/models/KorrekturWaermeerzeugerHeizung';
import { createValidator, createValidators, validateNotEmpty } from '@/helpers/model_validators';
import KpSelectField from '@/components/KpSelectField.vue';
import KpEnergieWaermequelleSelect from '@/components/KpEnergieWaermequelleSelect.vue';
import KpWaermeerzeugerHeizungSelect from '@/components/KpWaermeerzeugerHeizungSelect.vue';
import KpTextAreaField from '@/components/KpTextAreaField.vue';

const props = defineProps<{
    // id is injected via router:
    id: number | 'new';
}>();

const router = useRouter();
const toast = useToast();
const record: Ref<KorrekturWaermeerzeugerHeizung | null> = ref(null);
const saving = ref(false);

const { allValid, validators } = createValidators({
    kombination: createValidator(
        computed(() => record.value?.kombination),
        [validateNotEmpty],
    ),
});

onMounted(() => {
    loadRecord(props.id);
});

onBeforeRouteLeave(() => {
    return reallyCancel(record?.value);
});

onBeforeRouteUpdate((to, from) => {
    if (to.params.id !== from.params.id) {
        loadRecord(to.params.id === 'new' ? 'new' : Number(to.params.id));
    }
});

async function loadRecord(id: number | 'new') {
    if (id === 'new') {
        record.value = createModel(KorrekturWaermeerzeugerHeizung);
    } else {
        try {
            const rec = await KorrekturWaermeerzeugerHeizung.loadById(id);
            if (rec) {
                record.value = rec;
            } else {
                record.value = null;
                throw new Error(getI18n().t('record_not_found'));
            }
        } catch (e) {
            errToToast(e, toast);
        }
    }
}

async function onSave() {
    if (record?.value === null) {
        return;
    }
    saving.value = true;
    try {
        const isNew = !Number(record.value.id);
        await record.value.save();
        record.value.commit();
        toast.add({
            severity: 'success',
            detail: getI18n().t('datensatz_gespeichert'),
            life: 1000,
        });
        if (isNew) {
            router.push({ name: 'bg_geb_korrektur_waermeerzeuger_heizung_edit', params: { id: record.value.id } });
        }
    } catch (e) {
        errToToast(e, toast);
    } finally {
        saving.value = false;
    }
}

function onCancel() {
    if (reallyCancel(record?.value)) {
        record?.value?.rollback();
        router.push({ name: 'bg_geb_korrektur_waermeerzeuger_heizung' });
    }
}
</script>
<template>
    <div v-if="!record" class="flex justify-content-center">
        <ProgressSpinner></ProgressSpinner>
    </div>
    <div v-else>
        <Card class="kp-max-content-width">
            <template #header>
                <Toolbar class="toolbar">
                    <template #start>
                        <h2>
                            <i class="fas fa-user"></i> {{ $t('korr_waermeerzeuger_heizung') }}:
                            {{ record.id || $t('new') }}
                        </h2>
                    </template>
                    <template #end>
                        <div class="flex gap-1">
                            <KpButton
                                severity="success"
                                icon="far fa-floppy-disk"
                                v-tooltip="$t('speichern')"
                                :loading="saving"
                                :disabled="!allValid"
                                @click="onSave"
                            />
                            <KpButton
                                severity="secondary"
                                icon="far fa-xmark"
                                v-tooltip="$t('abbrechen')"
                                :disabled="saving"
                                @click="onCancel"
                            />
                        </div>
                    </template>
                </Toolbar>
            </template>
            <template #content>
                <div class="formgrid grid align-items-start">
                    <KpNumberField
                        v-model="record.kombination"
                        :label="$t('kombination')"
                        class="col-6"
                        fluid
                        :invalid="!validators.kombination.isValid.value"
                        :error="validators.kombination.msg.value"
                    ></KpNumberField>
                </div>
                <div class="formgrid grid align-items-start">
                    <KpWaermeerzeugerHeizungSelect
                        :label="$t('waermeerzeuger_heizung_1')"
                        v-model="record.waermeerzeuger_heizung_1_id"
                        class="col-6"
                        fluid
                    ></KpWaermeerzeugerHeizungSelect>
                    <KpEnergieWaermequelleSelect
                        v-model="record.energie_waermequelle_1_id"
                        class="col-6"
                        fluid
                        :label="$t('energie_waermequelle_heizung_1')"
                    ></KpEnergieWaermequelleSelect>
                </div>
                <div class="formgrid grid align-items-start">
                    <KpWaermeerzeugerHeizungSelect
                        :label="$t('waermeerzeuger_heizung_2')"
                        v-model="record.waermeerzeuger_heizung_2_id"
                        class="col-6"
                        fluid
                    ></KpWaermeerzeugerHeizungSelect>
                    <KpEnergieWaermequelleSelect
                        v-model="record.energie_waermequelle_2_id"
                        class="col-6"
                        fluid
                        :label="$t('energie_waermequelle_heizung_2')"
                    ></KpEnergieWaermequelleSelect>
                </div>
                <div class="formgrid grid align-items-start">
                    <KpWaermeerzeugerHeizungSelect
                        :label="$t('korrektur_waermeerzeuger_heizung_1')"
                        v-model="record.korrektur_waermeerzeuger_heizung_1_id"
                        class="col-6"
                        fluid
                    ></KpWaermeerzeugerHeizungSelect>
                    <KpEnergieWaermequelleSelect
                        v-model="record.korrektur_energie_waermequelle_1_id"
                        class="col-6"
                        fluid
                        :label="$t('korrektur_energie_waermequelle_heizung_1')"
                    ></KpEnergieWaermequelleSelect>
                </div>
                <div class="formgrid grid align-items-start">
                    <KpWaermeerzeugerHeizungSelect
                        :label="$t('korrektur_waermeerzeuger_heizung_2')"
                        v-model="record.korrektur_waermeerzeuger_heizung_2_id"
                        class="col-6"
                        fluid
                    ></KpWaermeerzeugerHeizungSelect>
                    <KpEnergieWaermequelleSelect
                        v-model="record.korrektur_energie_waermequelle_2_id"
                        class="col-6"
                        fluid
                        :label="$t('korrektur_energie_waermequelle_heizung_2')"
                    ></KpEnergieWaermequelleSelect>
                </div>
                <div class="formgrid grid align-items-start">
                    <KpTextAreaField
                        v-model="record.bemerkung"
                        :label="$t('bemerkung')"
                        class="col-12"
                        fluid
                    ></KpTextAreaField>
                </div>
            </template>
        </Card>
    </div>
</template>

<style lang="css" scoped>
.toolbar {
    border: none;
    border-bottom: 1px solid var(--p-toolbar-border-color);
    h2 {
        margin: 0;
    }
}
</style>
